import { LoadingButton as Button } from '@mui/lab'
import {
  Alert,
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { IoClose, IoWarning } from 'react-icons/io5'

import { Quill } from '@/components'
import type { QuestionOption } from '@/types'

interface NewQuestionAddOptionProps {
  open: boolean
  onCancel?: () => void
  onConfirm: (data: QuestionOption) => void
  alternatives: QuestionOption[]
}

const MINIMUM_FEEDBACK_LENGTH = 10

function NewQuestionAddOption({
  open,
  onConfirm,
  onCancel,
  alternatives,
}: NewQuestionAddOptionProps) {
  const theme = useTheme()
  const [response, setResponse] = useState<string>('')
  const [feedback, setFeedback] = useState<string>('')
  const [isCorrect, setIsCorrect] = useState<boolean>(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (open) {
      setResponse('')
      setFeedback('')
      setIsCorrect(false)
      setIsSubmitting(false)
    }
  }, [open])

  const validationState = useMemo(() => {
    const hasContent = response.trim().length > 0
    const hasFeedback = feedback.trim().length >= MINIMUM_FEEDBACK_LENGTH
    const hasCorrectAnswer = alternatives.some((alt) => alt.its_correct)
    const isLastOption = alternatives.length === 4
    const wouldCreateDuplicateCorrect = hasCorrectAnswer && isCorrect

    const errors: string[] = []

    if (!hasContent) {
      errors.push('Conteúdo da alternativa é obrigatório')
    }

    if (!hasFeedback) {
      errors.push(`Feedback deve ter pelo menos ${MINIMUM_FEEDBACK_LENGTH} caracteres`)
    }

    if (wouldCreateDuplicateCorrect) {
      errors.push(
        'Já existe uma alternativa correta. Apenas uma resposta pode ser correta.',
      )
    }

    if (isLastOption && !hasCorrectAnswer && !isCorrect) {
      errors.push('É necessário pelo menos uma alternativa correta.')
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings:
        isLastOption && !hasCorrectAnswer && !isCorrect
          ? ['Esta é a última alternativa e nenhuma foi marcada como correta ainda.']
          : [],
    }
  }, [response, feedback, isCorrect, alternatives])

  const handleSaveOption = useCallback(async () => {
    if (!validationState.isValid) {
      validationState.errors.forEach((error) => toast.error(error))
      return
    }

    setIsSubmitting(true)

    try {
      const newOption: QuestionOption = {
        its_correct: isCorrect,
        option: response.trim(),
        id: Date.now() + Math.random(),
        feedback: feedback.trim(),
        order: alternatives.length + 1,
      }

      onConfirm(newOption)
      toast.success('Alternativa adicionada com sucesso!')
    } catch (error) {
      toast.error('Erro ao adicionar alternativa')
      setIsSubmitting(false)
      console.error(error) // eslint-disable-line
    }
  }, [validationState, isCorrect, response, feedback, alternatives.length, onConfirm])

  const handleClose = useCallback(() => {
    if (!isSubmitting && onCancel) {
      onCancel()
    }
  }, [isSubmitting, onCancel])

  const handleIsCorrectChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setIsCorrect(event.target.value === 'true')
    },
    [],
  )

  const alternativeNumber = alternatives.length + 1
  const alternativeLetter = String.fromCharCode(96 + alternativeNumber).toUpperCase()

  return (
    <Dialog
      maxWidth="md"
      fullWidth
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: { borderRadius: 3, maxHeight: '90vh' },
      }}>
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
        }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Adicionar Alternativa
          </Typography>
          <Chip
            label={`Alternativa ${alternativeLetter}`}
            color="primary"
            variant="outlined"
            size="small"
          />
        </Stack>
        <IconButton
          onClick={handleClose}
          disabled={isSubmitting}
          sx={{ color: 'text.secondary' }}>
          <IoClose />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ p: 3 }}>
        <Stack spacing={3}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              backgroundColor: theme.palette.primary.light + '10',
              borderRadius: 2,
            }}>
            <Typography variant="body2" color="text.secondary">
              Alternativa {alternativeNumber} de 5 • {alternatives.length} já adicionadas
            </Typography>
          </Paper>

          {validationState.errors.length > 0 && (
            <Alert severity="error" sx={{ borderRadius: 2 }}>
              <Stack spacing={1}>
                {validationState.errors.map((error, index) => (
                  <Typography key={index} variant="body2">
                    • {error}
                  </Typography>
                ))}
              </Stack>
            </Alert>
          )}

          {validationState.warnings.length > 0 && (
            <Alert severity="warning" sx={{ borderRadius: 2 }} icon={<IoWarning />}>
              <Stack spacing={1}>
                {validationState.warnings.map((warning, index) => (
                  <Typography key={index} variant="body2">
                    • {warning}
                  </Typography>
                ))}
              </Stack>
            </Alert>
          )}

          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              Conteúdo da Alternativa {alternativeLetter}
            </Typography>
            <Quill value={response} onChange={setResponse} />
          </Box>

          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              Esta é a resposta correta?
            </Typography>
            <RadioGroup value={isCorrect.toString()} onChange={handleIsCorrectChange} row>
              <FormControlLabel
                value="true"
                control={<Radio color="success" />}
                label="Sim, é a resposta correta"
                disabled={alternatives.some((alt) => alt.its_correct)}
              />
              <FormControlLabel
                value="false"
                control={<Radio color="error" />}
                label="Não, é incorreta"
              />
            </RadioGroup>

            {alternatives.some((alt) => alt.its_correct) && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 1, display: 'block' }}>
                Já existe uma alternativa marcada como correta.
              </Typography>
            )}
          </Box>

          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              Feedback da Alternativa
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Explique por que esta alternativa está correta ou incorreta (mínimo{' '}
              {MINIMUM_FEEDBACK_LENGTH} caracteres)
            </Typography>
            <Quill value={feedback} onChange={setFeedback} />
            <Typography
              variant="caption"
              color={
                feedback.trim().length >= MINIMUM_FEEDBACK_LENGTH
                  ? 'success.main'
                  : 'text.secondary'
              }
              sx={{ mt: 1, display: 'block' }}>
              {feedback.trim().length}/{MINIMUM_FEEDBACK_LENGTH} caracteres
            </Typography>
          </Box>
        </Stack>
      </DialogContent>

      <Divider />

      <DialogActions
        sx={{
          p: 3,
          gap: 1,
          flexDirection: { xs: 'column', sm: 'row' },
        }}>
        <Button
          variant="contained"
          onClick={handleSaveOption}
          loading={isSubmitting}
          fullWidth
          disabled={!validationState.isValid}
          sx={{
            borderRadius: 2,
            width: { xs: '100%', sm: 'auto' },
          }}>
          {isSubmitting ? 'Adicionando...' : 'Adicionar Alternativa'}
        </Button>

        <Button
          variant="outlined"
          onClick={handleClose}
          disabled={isSubmitting}
          fullWidth
          sx={{
            borderRadius: 2,
            width: { xs: '100%', sm: 'auto' },
          }}>
          Cancelar
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default memo(NewQuestionAddOption)
